import { forwardRef, HTMLAttributes, useState } from 'react';
import LocalIcon, { LocalIconProps } from '../Icons/LocalIcon';
import LucideIcon, { LucideIconProps } from '../Icons/LucideIcon';
import { Button as ButtonUI } from '@/components/ui/button';
import { ColorType } from '@/src/styles/color';
import RadixIcon, { RadixIconProps } from '../Icons/RadixIcon';
import { twJoin } from 'tailwind-merge';

const buttonCommon =
  'p-0 transition-color inline-flex items-center justify-center rounded-md transition-transform hover:translate-y-[-2px]  focus-visible:border-gray9 focus-visible:outline-none focus-visible:ring-0  focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50';

const buttonSizeData = {
  large: 'h-[40px] w-[40px]',
  medium: 'h-[36px] w-[36px]',
  small: 'h-[32px] w-[32px]',
  xSmall: 'h-[24px] w-[24px]',
};

const buttonColorData = {
  primary:
    'border border-blue7 bg-blue7 hover:border-blue5 hover:bg-blue5 active:border-blue8 active:bg-blue8',
  secondary:
    'border border-blue1 bg-blue0 hover:border-blue1 hover:bg-blue1 active:border-blue7 disabled:border-blue0',
  outline:
    'border border-gray1 bg-white hover:border-gray2 hover:bg-gray1 active:border-gray4',
  red: 'border border-gray1 bg-white hover:border-red5 hover:bg-red0 active:bg-red1',
  ghost: 'border border-white bg-white hover:bg-gray1 active:border-gray4',
};

interface IconButtonProps extends HTMLAttributes<HTMLButtonElement> {
  buttonSize?: keyof typeof buttonSizeData;
  buttonColor?: keyof typeof buttonColorData;

  /**
   * @description lucide Icon 사용시
   */
  radixIconProps?: RadixIconProps;
  /**
   * @description lucide Icon 사용시
   */
  lucideIconProps?: LucideIconProps;
  /**
   * @description local SVG, 아이콘 사용시
   */
  localIconProps?: LocalIconProps;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      buttonSize = 'large',
      buttonColor = 'primary',
      lucideIconProps,
      localIconProps,
      radixIconProps,
      ...props
    },
    ref,
  ) => {
    const [isHover, setIsHover] = useState(false);

    const isHoverButtonStyle = isHover
      ? 'transform translate-y-[-2px]'
      : 'transform translate-y-0';

    const getIconColor = () => {
      if (buttonColor === 'primary') {
        return 'white';
      }

      if (buttonColor === 'secondary') {
        return 'blue7';
      }

      if (buttonColor === 'ghost' || buttonColor === 'outline') {
        return 'gray9';
      }

      if (buttonColor === 'red') {
        return 'red5';
      }
    };

    const renderIcon = () => {
      if (lucideIconProps) {
        return (
          <LucideIcon
            name={lucideIconProps.name}
            color={lucideIconProps.color ?? getIconColor()}
            size={lucideIconProps.size ?? 16}
            className="flex flex-shrink-0"
          />
        );
      }

      if (radixIconProps) {
        return (
          <RadixIcon
            name={radixIconProps.name}
            color={radixIconProps.color ?? (getIconColor() as ColorType)}
            size={radixIconProps.size ?? 16}
            className="flex flex-shrink-0"
          />
        );
      }

      if (localIconProps) {
        return (
          <LocalIcon
            iconSize={localIconProps.iconSize ?? 16}
            {...localIconProps}
          />
        );
      }
    };

    return (
      <ButtonUI
        ref={ref}
        className={twJoin(
          buttonCommon,
          buttonSizeData[buttonSize],
          buttonColorData[buttonColor],
          isHoverButtonStyle,
          className,
        )}
        type="button"
        onMouseOver={() => {
          setIsHover(true);
        }}
        onMouseLeave={() => {
          setIsHover(false);
        }}
        {...props}
      >
        {renderIcon()}
      </ButtonUI>
    );
  },
);

export default IconButton;
